/**
 * @fileoverview Card showing current user profile (/auth/me), orgs, and reporting currency.
 * @module features/settings/components/AccountInfoCard
 */

import { ReactElement, useMemo, useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent, Button } from '@/shared/components/ui'
import { Popover, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { MeSchema, type MeProfile } from '@/features/settings/schemas/meSchemas'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'
import {
  OrgSettingsResponseSchema,
  getOrgSettings,
  updateOrgSettings,
} from '@/features/settings/api/orgSettingsService'
import { prefixWithOrg } from '@/shared/utils/queryKeys'
import { OrgMembersPopover } from './OrgMembersPopover'
import { z } from 'zod'

/** Member role enumeration */
const _MemberRoleSchema = z.enum(['OWNER', 'ADMIN', 'MEMBER'])
type MemberRole = z.infer<typeof _MemberRoleSchema>

/**
 * Displays current signed-in user, organizations list, and reporting currency selector.
 *
 * @component
 * @returns ReactElement
 * @example
 * ```tsx
 * <AccountInfoCard />
 * ```
 */
export function AccountInfoCard(): ReactElement {
  const { t: tSettings } = useTranslation('features-settings')
  const { activeOrgId } = useActiveOrg()
  const qc = useQueryClient()
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null)
  const [popoverOrgId, setPopoverOrgId] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery<MeProfile, Error>({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const res = await apiClient.get('/auth/me')
      return MeSchema.parse(res.data)
    },
    staleTime: 5 * 60 * 1000,
  })

  const settingsQueryKey = useMemo(
    () => (activeOrgId ? prefixWithOrg(activeOrgId, ['settings']) : ['settings', 'no-org']),
    [activeOrgId]
  )

  const {
    data: orgSettings,
    isLoading: isSettingsLoading,
    error: settingsError,
  } = useQuery({
    queryKey: settingsQueryKey,
    queryFn: async () => {
      if (!activeOrgId) throw new Error('No active organization')
      const orgId = OrgIdSchema.parse(activeOrgId)
      const res = await getOrgSettings(orgId)
      return OrgSettingsResponseSchema.parse(res)
    },
    enabled: Boolean(activeOrgId),
    staleTime: 5 * 60 * 1000,
  })

  const mutation = useMutation({
    mutationFn: async (nextCurrency: string) => {
      if (!activeOrgId) throw new Error('No active organization')
      const orgId = OrgIdSchema.parse(activeOrgId)
      return updateOrgSettings(orgId, { reportingCurrency: nextCurrency })
    },
    onSuccess: async _data => {
      toast.success(tSettings('cards.reportingCurrency.toastSaved'))
      await qc.invalidateQueries({ queryKey: settingsQueryKey })
      setSelectedCurrency(null)
    },
    onError: (e: unknown) => {
      const msg = (e as Error)?.message ?? tSettings('cards.reportingCurrency.errors.updateFailed')
      toast.error(msg)
    },
  })

  const currentCurrency = orgSettings?.reportingCurrency
  const allowed = orgSettings?.allowedReportingCurrencies ?? []
  const pendingValue = selectedCurrency ?? currentCurrency ?? ''

  const myOrgRole: MemberRole | undefined = (() => {
    if (!data || !popoverOrgId) return undefined
    return (
      (data.organizations.find(o => (o.id as unknown as string) === popoverOrgId)
        ?.role as MemberRole) || undefined
    )
  })()

  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <CardTitle>{tSettings('cards.account.title')}</CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-5 w-56" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-64" />
          </div>
        ) : error ? (
          <div className="text-sm text-foreground">{tSettings('cards.account.errors.loadProfile')}</div>
        ) : data ? (
          <div className="space-y-4">
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">
                {tSettings('cards.account.labels.email', { defaultValue: 'Email' })}
              </div>
              <div className="text-foreground" aria-live="polite">
                {data.email ?? '—'}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">
                {tSettings('cards.account.labels.preferredLanguage')}
              </div>
              <div className="text-foreground">{data.preferredLanguage ?? '—'}</div>
            </div>

            {activeOrgId && (
              <div className="space-y-2">
                <div className="text-sm text-muted-foreground">
                  {tSettings('cards.reportingCurrency.title')}
                </div>
                {isSettingsLoading ? (
                  <Skeleton className="h-10 w-32" />
                ) : settingsError ? (
                  <div className="text-sm text-foreground">
                    {tSettings('cards.reportingCurrency.errors.loadSettings')}
                  </div>
                ) : orgSettings ? (
                  <div className="flex items-center gap-3">
                    <Select value={pendingValue} onValueChange={setSelectedCurrency}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {allowed.map(code => (
                          <SelectItem key={code} value={code}>
                            {code}
                            {code === currentCurrency && (
                              <Badge variant="secondary" className="ml-2 text-xs">
                                current
                              </Badge>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedCurrency && selectedCurrency !== currentCurrency && (
                      <Button
                        type="button"
                        onClick={() => !mutation.isPending && mutation.mutate(selectedCurrency)}
                        disabled={mutation.isPending}
                        size="sm"
                      >
                        {mutation.isPending
                          ? tSettings('cards.reportingCurrency.buttons.saving')
                          : tSettings('cards.reportingCurrency.buttons.save')}
                      </Button>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">
                {tSettings('cards.account.labels.organizations')}
              </div>
              {data.organizations.length === 0 ? (
                <div className="text-sm text-foreground">
                  {tSettings('cards.account.empty.noOrganizations')}
                </div>
              ) : (
                <ul className="space-y-2">
                  {data.organizations.map(org => (
                    <li key={org.id as unknown as string}>
                      <Popover
                        open={popoverOrgId === (org.id as unknown as string)}
                        onOpenChange={open => setPopoverOrgId(open ? (org.id as unknown as string) : null)}
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="w-full border border-border rounded-md p-3 hover:bg-accent/5 transition-colors text-left cursor-pointer"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-foreground font-medium">{org.name}</span>
                                  {data.defaultOrgId === (org.id as unknown as string) && (
                                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                      {tSettings('cards.account.labels.default', { defaultValue: 'Default' })}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {tSettings('cards.account.labels.role')}: {org.role ?? '—'}
                                </div>
                              </button>
                            </PopoverTrigger>
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            {tSettings('cards.account.hints.manageMembers')}
                          </TooltipContent>
                        </Tooltip>
                        <OrgMembersPopover
                          orgId={org.id as unknown as string}
                          currentUserId={data.userId}
                          currentUserRole={myOrgRole}
                        />
                      </Popover>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
